// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngSticky from "@guestsupply/App/Components/XngSticky";
import { mergeWithCss, xScreen } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useMemo, useState } from "react";
import { css } from "styled-components";

interface CartPageTemplateProps {
    children: React.ReactNode;
    extendedStyles?: CartPageTemplateStyles;
    isReview?: boolean;
}

interface Props {
    MainSection: React.FC<any>;
    HeaderSection: React.FC<any>;
    CartMessages: React.FC<any>;
    CartFulfillmentSelector: React.FC<any>;
    CartTotalSection: React.FC<any>;
    ActionsSection: React.FC<any>;
}

interface CartPageTemplateContextProps {
    styles: CartPageTemplateStyles;
    isReview?: boolean;
}

export interface CartPageTemplateStyles {
    container?: InjectableCss;
    headerSection?: InjectableCss;
    mainSection?: InjectableCss;
    cartMessages?: InjectableCss;
    cartFulfullmentSelector?: InjectableCss;
    totalSection?: InjectableCss;
    actionsSection?: InjectableCss;
    xngStickyStyles?: InjectableCss;
}

const CartPageTemplateContext = React.createContext<CartPageTemplateContextProps>({
    styles: {},
    isReview: false,
});

export const useCartPageTemplateContext = () => {
    const context = React.useContext(CartPageTemplateContext);
    if (!context) {
        throw new Error("CartPageTemplate compound components cannot be rendered outside CartPageTemplate");
    }
    return context;
};

export const cartPageTemplateBreakpoint = 768;
export const cartPageTemplateStyles: CartPageTemplateStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            position: relative;
            min-height: 300px;
            @media print {
                display: block;
            }
        `,
    },
    headerSection: {
        css: css`
            width: 100%;
        `,
    },
    actionsSection: {
        css: css`
            width: 100%;
        `,
    },
    mainSection: {
        css: css`
            width: calc(100% - 320px);
            padding-right: 20px;
            ${xScreen("lte", "md")} {
                padding-right: 16px;
                width: calc(100% - 232px);
            }

            ${xScreen("lt", cartPageTemplateBreakpoint)} {
                width: 100%;
                padding: 0;
                margin-bottom: 0;
            }
            @media print {
                width: 100%;
            }
        `,
    },
    totalSection: {
        css: css`
            width: 320px;
            ${xScreen("lte", "md")} {
                width: 232px;
            }
            ${xScreen("lt", cartPageTemplateBreakpoint)} {
                width: 100%;
                display: flex;
                justify-content: center;
                order: 4;
                margin-top: 20px;
                & > div.xng-sticky {
                    max-width: 100%;
                }
            }

            ${xScreen("lte", "xs")} {
                & > div.xng-sticky {
                    max-width: 100%;
                }
            }

            @media print {
                & > div.xng-sticky {
                    position: static;
                }
            }
        `,
    },
    cartMessages: {
        css: css`
            &:has(div[class^="Wrapper-sc"]:empty) {
                display: none;
            }
        `,
    },
    cartFulfullmentSelector: {
        css: css`
            margin-bottom: 80px;
            &:has(div[class^="Wrapper-sc"]:empty) {
                display: none;
            }
        `,
    },
    xngStickyStyles: {
        css: css`
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
};

const CartPageTemplate: FC<CartPageTemplateProps> & Props = ({ children, extendedStyles, isReview }) => {
    const [styles] = useState(() => mergeWithCss(cartPageTemplateStyles, extendedStyles));
    const value = useMemo(() => ({ styles, isReview }), [styles, isReview]);
    return (
        <CartPageTemplateContext.Provider value={value}>
            <StyledWrapper {...styles.container}>{children}</StyledWrapper>
        </CartPageTemplateContext.Provider>
    );
};

const HeaderSection: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.headerSection}>{children}</StyledWrapper>;
};

const CartMessages: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.cartMessages}>{children}</StyledWrapper>;
};

const CartFulfillmentSelector: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.cartFulfullmentSelector}>{children}</StyledWrapper>;
};

const MainSection: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.mainSection}>{children}</StyledWrapper>;
};

const CartTotalSection: FC = ({ children }) => {
    const { styles, isReview } = useCartPageTemplateContext();

    return (
        <StyledWrapper {...styles.totalSection} {...{ isReview }}>
            <XngSticky {...styles.xngStickyStyles}>{children}</XngSticky>
        </StyledWrapper>
    );
};

const ActionsSection: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.actionsSection}>{children}</StyledWrapper>;
};

CartPageTemplate.HeaderSection = HeaderSection;
CartPageTemplate.MainSection = MainSection;
CartPageTemplate.CartMessages = CartMessages;
CartPageTemplate.CartFulfillmentSelector = CartFulfillmentSelector;
CartPageTemplate.CartTotalSection = CartTotalSection;
CartPageTemplate.ActionsSection = ActionsSection;

export default CartPageTemplate;
